import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DeepLinkService } from '../shared/deep-link.service';
import { uiLocale } from '../shared/chart.utils';

/** GET /api/admin/cams/status (admin session; also without TOTP). Every field optional: fail soft. */
interface CamsStatus {
  configured?: { url?: boolean; apiKey?: boolean; totp?: boolean; pin?: boolean; allowlist?: number };
  /** Env var names still missing, and further configuration problems (backend texts, English). */
  missing?: string[];
  problems?: string[];
  legacyPassword?: boolean;
  protectUrl?: string;
  /** Leaf certificate SHA-256 seen by the TLS probe: shown when no pin is set, or when it does not match the pin. */
  pinObserved?: string | null;
  pinMismatch?: boolean;
  /** Only when pin and API key are set; `pending`: the backend's first check is still running. */
  protect?: { reachable?: boolean | null; version?: string | null; error?: string; pending?: boolean } | null;
  /** A Protect check (version / certificate probe) is still running: ask again shortly. */
  pending?: boolean;
  /** The admin login has its second factor (TOTP): only then does the backend serve camera data. */
  totp?: boolean;
}

/** GET /api/admin/cams (login with TOTP): the allowlisted cameras. */
interface CamInfo {
  id:     string;
  name?:  string;
  type?:  string;
  state?: string;          // CONNECTED | CONNECTING | DISCONNECTED
  supportFullHdSnapshot?: boolean;
}

/** GET /api/admin/cams/all (login with TOTP): every camera, to choose CAMS_ALLOWLIST. No images. */
interface CamAllInfo extends CamInfo { allowlisted?: boolean; }

/** GET /api/admin/cams/audit (login with TOTP): in-memory ring on the backend. */
interface CamAuditEntry {
  ts:       number;
  action:   string;        // login | login-failed | logout | snapshot-burst-start
  session?: string;        // hash prefix, never the token
  ip?:      string;
  [k: string]: unknown;
}

/** One camera of the grid; `url` is an object URL of the latest snapshot (RAM only). */
interface CamTile {
  cam:     CamInfo;
  name:    string;
  online:  boolean;        // state CONNECTED (or unknown): snapshots are requested
  url:     string | null;
  at:      string;         // HH:MM:SS of the shown image
  offline: boolean;        // Protect answered 503, or the camera is not connected
  error:   string | null;
}

interface CfgRow { label: string; env: string; ok: boolean; warn?: boolean; value: string; }
interface AuditRow { key: number; time: string; full: string; action: string; label: string; cls: string; session: string; ip: string; }
interface AllRow { c: CamAllInfo; name: string; selected: boolean; }

const STATUS_MS = 20_000;   // while shown: refresh the status (Protect reachability, certificate)
const LIST_MS   = 30_000;   // refresh the camera list (states) at most this often
const GRID_MS   = 3_000;    // grid snapshot rounds (after the previous round settled)
const LARGE_MS  = 1_500;    // large view (hq=1)
const DECODE_MS = 2_000;    // longest wait for a decoded snapshot before it is shown anyway
const PENDING_MS = 3_000;   // status said a Protect check is still running: ask again after this
const PARALLEL  = 2;       // snapshot requests in flight per round (the backend caps Protect at 3 / ~5 per s)
const CAM_ID    = /^[0-9a-f]{24}$/;
/** Deep-link key of the large view's camera (the dashboard owns "admin=cams" itself). */
const LINK_CAM  = 'admin.cams.cam';

/**
 * Admin → Kameras: UniFi Protect snapshots. The second factor (TOTP) is part of the admin login itself, so a
 * valid session shows the grid directly. Without a TOTP secret on the backend (password-only login) only the
 * status is shown (what is configured, the observed certificate fingerprint) plus an explanation — no images.
 * Snapshots are fetched with fetch() + X-Session-Token as Blobs and shown via object URLs that are revoked
 * as soon as they are replaced, on logout and close — nothing is written to storage, and every request
 * uses cache: 'no-store'. Images refresh only while this section is open and the browser tab is visible;
 * the component only exists while the admin overlay shows this section (ngOnDestroy stops all).
 */
@Component({
  selector: 'app-admin-cams',
  templateUrl: './admin-cams.component.html',
  // The dashboard ticks every second; OnPush keeps the grid out of those cycles.
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow: hidden; }
    .camx-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; -webkit-overflow-scrolling: touch; }
    .camx-bar { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem .5rem; }
    .camx-hint { font-size: .7rem; color: var(--bs-secondary-color); }
    .camx-small { font-size: .78rem; }
    .camx-card-title { font-size: .7rem; letter-spacing: .05em; text-transform: uppercase; color: var(--bs-secondary-color); font-weight: 600; }
    .camx-kv { display: grid; grid-template-columns: minmax(0, max-content) minmax(0, 1fr); gap: .3rem .75rem; align-items: baseline; }
    .camx-env { font-family: var(--bs-font-monospace); font-size: .66rem; color: var(--bs-secondary-color); word-break: break-all; }
    .mono { font-family: var(--bs-font-monospace); }
    .camx-fp { font-family: var(--bs-font-monospace); font-size: .72rem; word-break: break-all; user-select: all;
               background: var(--bs-tertiary-bg); border-radius: 4px; padding: .35rem .5rem; }
    .camx-state { font-size: .62rem; font-weight: 600; }
    .camx-toggle { background: none; border: 0; padding: 0; color: inherit; text-align: left; width: 100%;
                   display: flex; align-items: center; gap: .4rem; }
    .camx-toggle .camx-chev { transition: transform .15s; display: inline-block; width: .7em; color: var(--bs-secondary-color); }
    .camx-toggle.open .camx-chev { transform: rotate(90deg); }

    /* Grid */
    .camx-grid { display: grid; gap: .6rem; grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr)); }
    .camx-tile { display: block; width: 100%; padding: 0; text-align: left; color: inherit; background: var(--bs-body-bg);
                 border: 1px solid var(--bs-border-color); border-radius: 8px; overflow: hidden; cursor: pointer; }
    .camx-tile:focus-visible { outline: 2px solid var(--bs-primary); outline-offset: 2px; }
    .camx-frame { position: relative; aspect-ratio: 16 / 9; background: #000; }
    .camx-frame img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; }
    .camx-ph { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
               gap: .25rem; color: rgba(255,255,255,.6); font-size: .75rem; text-align: center; padding: .5rem; }
    .camx-ph-icon { font-size: 1.6rem; opacity: .6; }
    .camx-age { position: absolute; right: .35rem; bottom: .3rem; font-size: .62rem; color: #fff; background: rgba(0,0,0,.55);
                border-radius: 3px; padding: 0 .3rem; font-variant-numeric: tabular-nums; }
    .camx-cap { display: flex; align-items: center; gap: .4rem; padding: .35rem .5rem; font-size: .8rem; min-width: 0; }
    .camx-cap .camx-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
    .camx-err { font-size: .64rem; color: var(--bs-danger-text-emphasis); padding: 0 .5rem .35rem; word-break: break-word; }

    /* Large view */
    .camx-large { position: fixed; inset: 0; z-index: 1060; background: #000; display: flex; flex-direction: column; }
    .camx-large-head { display: flex; align-items: center; gap: .5rem; padding: .5rem .75rem; color: #fff; flex-shrink: 0; min-width: 0; }
    .camx-large-title { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; gap: .5rem; }
    .camx-large-meta { display: inline-flex; align-items: center; gap: .5rem; flex-shrink: 0; }
    .camx-large-head .camx-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
    .camx-large-body { position: relative; flex: 1 1 auto; min-height: 0; }
    .camx-large-body img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }

    /* All cameras / audit */
    .camx-list > * + * { border-top: 1px solid var(--bs-border-color-translucent); }
    .camx-row { display: flex; gap: .5rem; align-items: flex-start; padding: .4rem 0; font-size: .78rem; min-width: 0; }
    .camx-row .form-check-input { margin-top: .15rem; flex-shrink: 0; }
    .camx-id { font-family: var(--bs-font-monospace); font-size: .66rem; color: var(--bs-secondary-color); word-break: break-all; }
    .camx-envline { font-family: var(--bs-font-monospace); font-size: .7rem; word-break: break-all; user-select: all;
                    background: var(--bs-tertiary-bg); border-radius: 4px; padding: .35rem .5rem; }
    .camx-audit { font-size: .72rem; }
    .camx-audit .camx-row { font-size: .72rem; flex-wrap: wrap; gap: .15rem .6rem; align-items: center; }

    /* Phones: no text under 11 px, touch-sized toggles and checkboxes, the large view clears notch and home indicator */
    @media (max-width: 575.98px) {
      .camx-env, .camx-state, .camx-age, .camx-err, .camx-id { font-size: .6875rem; }
      .camx-toggle { min-height: var(--dash-touch); }
      .camx-toggle .badge { font-size: .6875rem; }
      .camx-row { padding: .55rem 0; }
      .camx-row .form-check-input { width: 1.25em; height: 1.25em; margin-top: 0; }
      .camx-large-head { padding: calc(.4rem + env(safe-area-inset-top, 0px)) max(.5rem, env(safe-area-inset-right, 0px))
                                  .4rem max(.75rem, env(safe-area-inset-left, 0px)); }
      .camx-large-title { flex-direction: column; align-items: flex-start; gap: .1rem; }
      .camx-large-title .camx-name { width: 100%; }
      .camx-large { padding-bottom: env(safe-area-inset-bottom, 0px); }
    }
  `],
})
export class AdminCamsComponent implements OnInit, OnDestroy {
  /** Auth headers of the admin session (X-Session-Token), supplied by the dashboard. */
  @Input() authHeaders: () => Record<string, string> = () => ({});
  /** The admin session is gone (401): the dashboard drops it and shows the login prompt. */
  @Output() unauthorized = new EventEmitter<void>();
  /** Status says the legacy hard-coded admin password is still accepted (ADMIN_PW_HASH unset). */
  @Output() legacyPassword = new EventEmitter<boolean>();

  // ── Status ──
  status: CamsStatus | null = null;
  statusError: string | null = null;
  cfgRows: CfgRow[] = [];
  cfgComplete = false;
  cfgOpen = false;
  pinObserved = '';
  pinMismatch = false;
  problems: string[] = [];
  protectUrl = 'https://192.168.4.99';

  // ── Camera data ──
  /** The status says the login has TOTP: the backend serves camera data, the grid runs. */
  active = false;
  /**
   * Bumped whenever `active` changes. A list / audit response is used only if the epoch is still the one its
   * request started in — an answer that predates a deactivation must not refill camera metadata.
   */
  private epoch = 0;
  private pendingRetry: ReturnType<typeof setTimeout> | null = null;

  // ── Grid / large view ──
  tiles: CamTile[] = [];
  camsLoaded = false;
  camsError: string | null = null;
  large: { tile: CamTile; url: string | null; at: string; offline: boolean; error: string | null } | null = null;
  /** Camera named by the link: opened once the list is loaded (login with TOTP), dropped if it is not in it. */
  private linkedCam: string | null = null;

  // ── All cameras / audit ──
  allOpen = false;
  allRows: AllRow[] = [];
  allLoading = false;
  allError: string | null = null;
  allowlistLine = '';
  auditOpen = false;
  auditRows: AuditRow[] = [];
  auditLoading = false;
  auditError: string | null = null;

  copied: string | null = null;

  private camsAt = 0;
  private gen = 0;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private abort: AbortController | null = null;
  private retiring = new Set<string>();
  private destroyed = false;
  private subs: Subscription[] = [];
  private readonly onVisibility = () => {
    if (document.visibilityState !== 'hidden' && this.active) this.fetchStatus();
    this.syncLoops();
  };

  constructor(readonly translate: TranslateService, private readonly cdr: ChangeDetectorRef,
              private readonly link: DeepLinkService) {}

  ngOnInit(): void {
    this.linkedCam = this.camFromLink();
    if (!this.linkedCam && this.link.get(LINK_CAM) != null) this.link.set(LINK_CAM, null);   // not a camera id
    this.subs.push(this.link.external$.subscribe(() => this.applyLink()));
    this.subs.push(this.translate.onLangChange.subscribe(() => {
      if (this.status) this.buildCfg(this.status);
      this.auditRows = this.auditRows.map(r => ({ ...r, label: this.auditLabel(r.action) }));
      this.cdr.markForCheck();
    }));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.fetchStatus();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.pendingRetry) { clearTimeout(this.pendingRetry); this.pendingRetry = null; }
    this.stopLoops();
    this.revokeAll();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.subs.forEach(s => s.unsubscribe());
    // Overlay closed or another admin section shown: the camera goes with it (a lost session keeps it for the login)
    if (this.link.get('admin') !== 'cams') this.link.set(LINK_CAM, null);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.large) this.closeLarge();
  }

  trackTile = (_: number, t: CamTile) => t.cam.id;
  trackAll  = (_: number, r: AllRow) => r.c.id;
  trackAudit = (_: number, r: AuditRow) => r.key;
  trackCfg  = (_: number, r: CfgRow) => r.env;

  // ── Status ──────────────────────────────────────────────────────────────────

  refresh(): void {
    this.fetchStatus();
    if (this.active) { this.camsAt = 0; this.syncLoops(); }
  }

  private async fetchStatus(): Promise<void> {
    let r: Response | null;
    try { r = await this.camFetch('/api/admin/cams/status'); }
    catch (e: any) { this.statusError = e?.message || String(e); this.cdr.markForCheck(); return; }
    if (!r || this.destroyed) return;
    if (!r.ok) {
      this.statusError = await this.errorText(r);
      this.cdr.markForCheck();
      return;
    }
    let s: CamsStatus;
    try { s = await r.json() as CamsStatus; } catch { this.statusError = 'JSON'; this.cdr.markForCheck(); return; }
    if (this.destroyed) return;
    if ((s.pending || s.protect?.pending) && !this.pendingRetry) {
      this.pendingRetry = setTimeout(() => { this.pendingRetry = null; if (!this.destroyed) this.fetchStatus(); }, PENDING_MS);
    }
    const first = !this.status;
    this.status = s;
    this.statusError = null;
    this.buildCfg(s);
    if (first) this.cfgOpen = !this.cfgComplete || this.pinMismatch || this.problems.length > 0;
    this.legacyPassword.emit(!!s.legacyPassword);

    // Camera data only while the login has its second factor (the backend checks it on every request)
    if (this.totpConfigured && !this.active) this.activate();
    else if (!this.totpConfigured && this.active) this.deactivate();
    // Without it no camera can be shown: a linked one is dropped
    if (!this.totpConfigured && this.link.get(LINK_CAM) != null) { this.linkedCam = null; this.link.set(LINK_CAM, null); }
    this.cdr.markForCheck();
  }

  private buildCfg(s: CamsStatus): void {
    const c = s.configured ?? {};
    const t = (k: string, p?: object) => this.translate.instant(k, p) as string;
    const set = t('ADMIN_CAMS_SET'), missing = t('ADMIN_CAMS_MISSING');
    this.pinObserved = (s.pinObserved ?? '').trim();
    this.pinMismatch = typeof s.pinMismatch === 'boolean' ? s.pinMismatch : !!c.pin && !!this.pinObserved;
    this.problems = Array.isArray(s.problems) ? s.problems.filter(x => typeof x === 'string' && x) : [];
    this.protectUrl = s.protectUrl || 'https://192.168.4.99';
    const n = Number(c.allowlist) || 0;
    const p = s.protect;
    this.cfgRows = [
      { label: t('ADMIN_CAMS_CFG_APIKEY'), env: 'UNIFI_PROTECT_API_KEY', ok: !!c.apiKey, value: c.apiKey ? set : missing },
      { label: t('ADMIN_CAMS_CFG_TOTP'),   env: 'ADMIN_TOTP_SECRET',     ok: !!c.totp,   value: c.totp ? set : missing },
      { label: t('ADMIN_CAMS_CFG_PIN'),    env: 'UNIFI_PROTECT_CERT_SHA256', ok: !!c.pin && !this.pinMismatch, warn: this.pinMismatch,
        value: !c.pin ? missing : this.pinMismatch ? t('ADMIN_CAMS_PIN_MISMATCH') : set },
      { label: t('ADMIN_CAMS_CFG_ALLOWLIST'), env: 'CAMS_ALLOWLIST', ok: n > 0,
        value: n > 0 ? (n === 1 ? t('ADMIN_CAMS_ONE_CAM') : t('ADMIN_CAMS_N_CAMS', { n })) : t('ADMIN_CAMS_EMPTY') },
      { label: t('ADMIN_CAMS_CFG_PROTECT'), env: 'UNIFI_PROTECT_URL', ok: !!p?.reachable, warn: !p || !!p.pending,
        value: !p ? t('ADMIN_CAMS_PROTECT_NA')
                  : p.pending ? t('ADMIN_CAMS_PROTECT_PENDING')
                  : p.reachable ? t('ADMIN_CAMS_PROTECT_OK', { v: p.version || '?' })
                  : t('ADMIN_CAMS_PROTECT_DOWN') + (p.error ? ` (${p.error})` : '') },
      { label: t('ADMIN_CAMS_CFG_ADMIN_PW'), env: 'ADMIN_PW_HASH', ok: !s.legacyPassword, warn: !!s.legacyPassword,
        value: s.legacyPassword ? t('ADMIN_CAMS_PW_LEGACY') : t('ADMIN_CAMS_PW_PBKDF2') },
    ];
    this.cfgComplete = !!c.apiKey && !!c.totp && !!c.pin && !this.pinMismatch && n > 0;
  }

  /** The admin login has TOTP (status.totp; configured.totp says the same). */
  get totpConfigured(): boolean { return !!(this.status?.totp ?? this.status?.configured?.totp); }

  // ── Active / inactive ───────────────────────────────────────────────────────

  private activate(): void {
    this.epoch++;
    this.active = true;
    this.syncLoops();
    this.cdr.markForCheck();
  }

  /** No camera data (any more): stop every loop, abort requests, drop all images (and the camera metadata). */
  private deactivate(): void {
    this.epoch++;
    this.active = false;
    this.stopLoops();
    this.revokeAll();
    this.tiles = [];
    this.camsLoaded = false;
    this.camsAt = 0;
    this.camsError = null;
    this.large = null;
    this.allRows = [];
    this.allowlistLine = '';
    this.allOpen = false;
    this.allLoading = false;
    this.allError = null;
    this.auditRows = [];
    this.auditOpen = false;
    this.auditLoading = false;
    this.auditError = null;
    this.cdr.markForCheck();
  }

  // ── Loops ───────────────────────────────────────────────────────────────────

  /** (Re)starts exactly the loops the view needs; a new generation retires all older ones. */
  private syncLoops(): void {
    this.stopLoops();
    const gen = this.gen;
    if (this.destroyed || !this.active || document.visibilityState === 'hidden') return;
    this.abort = new AbortController();
    this.loop(gen, () => this.fetchStatus(), STATUS_MS, STATUS_MS);
    if (this.large) this.loop(gen, () => this.largeRound(gen), LARGE_MS, 0);
    else            this.loop(gen, () => this.gridRound(gen), GRID_MS, 0);
  }

  /** Runs `run` after `first` ms, then again `every` ms after each run settled (never two at a time). */
  private loop(gen: number, run: () => Promise<void>, every: number, first: number): void {
    const step = () => {
      if (gen !== this.gen || this.destroyed) return;
      run().catch(() => {}).finally(() => {
        if (gen !== this.gen || this.destroyed) return;
        this.later(step, every);
      });
    };
    if (first > 0) this.later(step, first); else step();
  }

  private later(fn: () => void, ms: number): void {
    const id = setTimeout(() => { this.timers.delete(id); fn(); }, ms);
    this.timers.add(id);
  }

  private stopLoops(): void {
    this.gen++;
    this.timers.forEach(t => clearTimeout(t));
    this.timers.clear();
    this.abort?.abort();
    this.abort = null;
  }

  private async gridRound(gen: number): Promise<void> {
    if (!this.camsLoaded || Date.now() - this.camsAt > LIST_MS) {
      await this.fetchCams(gen);
      if (gen !== this.gen) return;
      // A linked camera opens now that the list is there (restarts the loops: this round ends)
      if (this.linkedCam && this.camsLoaded && this.openLinked()) return;
    }
    const targets = this.tiles.filter(t => t.online);
    let i = 0;
    const worker = async () => {
      while (i < targets.length && gen === this.gen) await this.refreshTile(gen, targets[i++]);
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, targets.length) }, worker));
  }

  private async largeRound(gen: number): Promise<void> {
    const l = this.large;
    if (!l || !l.tile.online) return;
    const res = await this.fetchSnapshot(gen, l.tile.cam.id, true);
    if (gen !== this.gen || this.large !== l || !res) return;
    if (res.url) {
      this.retire(l.url);
      l.url = res.url;
      l.at = this.clock();
      l.offline = false;
      l.error = null;
    } else {
      l.offline = res.offline;
      l.error = res.error;
      if (res.offline) { this.retire(l.url); l.url = null; }
    }
    this.cdr.markForCheck();
  }

  // ── Cameras / snapshots ─────────────────────────────────────────────────────

  private async fetchCams(gen: number): Promise<void> {
    let r: Response | null;
    try { r = await this.camFetch('/api/admin/cams', {}, true); }
    catch (e: any) { if (gen === this.gen) { this.camsError = e?.message || String(e); this.cdr.markForCheck(); } return; }
    if (!r || gen !== this.gen) return;
    if (!r.ok) {
      this.camsError = await this.errorText(r);
      this.cdr.markForCheck();
      return;
    }
    let data: any;
    try { data = await r.json(); } catch { if (gen === this.gen) { this.camsError = 'JSON'; this.cdr.markForCheck(); } return; }
    if (gen !== this.gen) return;
    const list: CamInfo[] = (Array.isArray(data) ? data : Array.isArray(data?.cameras) ? data.cameras : [])
      .filter((c: CamInfo) => c && typeof c.id === 'string' && CAM_ID.test(c.id));
    const old = new Map(this.tiles.map(t => [t.cam.id, t]));
    this.tiles = list.map(c => {
      const online = !c.state || c.state === 'CONNECTED';
      const t = old.get(c.id);
      old.delete(c.id);
      if (t) {
        t.cam = c;
        t.name = c.name || c.id;
        t.online = online;
        if (!online) { t.offline = true; this.retire(t.url); t.url = null; }
        return t;
      }
      return { cam: c, name: c.name || c.id, online, url: null, at: '', offline: !online, error: null };
    });
    old.forEach(t => this.retire(t.url));
    this.camsLoaded = true;
    this.camsAt = Date.now();
    this.camsError = null;
    this.cdr.markForCheck();
  }

  private async refreshTile(gen: number, t: CamTile): Promise<void> {
    const res = await this.fetchSnapshot(gen, t.cam.id, false);
    if (gen !== this.gen || !res) return;
    if (res.url) {
      this.retire(t.url);
      t.url = res.url;
      t.at = this.clock();
      t.offline = false;
      t.error = null;
    } else {
      t.offline = res.offline;
      t.error = res.error;
      if (res.offline) { this.retire(t.url); t.url = null; }
    }
    this.cdr.markForCheck();
  }

  /**
   * One snapshot as an object URL (decoded before it is handed out, so swapping it in never flickers).
   * null: nothing to change (aborted, session gone, rate-limited — keep the last image).
   */
  private async fetchSnapshot(gen: number, id: string, hq: boolean):
      Promise<{ url: string | null; offline: boolean; error: string | null } | null> {
    if (!CAM_ID.test(id)) return { url: null, offline: false, error: 'id' };
    try {
      const r = await this.camFetch(`/api/admin/cams/${id}/snapshot?hq=${hq ? 1 : 0}`, {}, true);
      if (!r || gen !== this.gen) return null;
      if (r.status === 429) return null;
      if (!r.ok) {
        const err = await this.readError(r);
        if (gen !== this.gen) return null;
        // 503 busy: the backend's Protect cap is reached — keep the last image, retry next round
        if (err.code === 'busy') return null;
        // 503 offline (Protect cannot deliver a snapshot of this camera): placeholder instead of an error
        if (r.status === 503 && (err.code === 'offline' || !err.code)) return { url: null, offline: true, error: null };
        return { url: null, offline: false, error: err.text };
      }
      if (!(r.headers.get('Content-Type') || '').toLowerCase().startsWith('image/')) {
        return { url: null, offline: false, error: this.translate.instant('ADMIN_CAMS_NOT_IMAGE') };
      }
      const blob = await r.blob();
      if (gen !== this.gen || this.destroyed) return null;
      const url = URL.createObjectURL(blob);
      try {
        const img = new Image();
        img.src = url;
        // decode() can be deferred indefinitely in a background document: never let it stall the round
        await Promise.race([img.decode(), new Promise<void>(res => setTimeout(res, DECODE_MS))]);
      } catch {
        URL.revokeObjectURL(url);
        return { url: null, offline: false, error: this.translate.instant('ADMIN_CAMS_NOT_IMAGE') };
      }
      if (gen !== this.gen || this.destroyed) { URL.revokeObjectURL(url); return null; }
      return { url, offline: false, error: null };
    } catch (e: any) {
      if (e?.name === 'AbortError' || gen !== this.gen) return null;
      return { url: null, offline: false, error: e?.message || String(e) };
    }
  }

  openLarge(t: CamTile): void {
    this.linkedCam = null;
    if (this.large) this.retire(this.large.url);   // a link switches cameras without closing
    this.large = { tile: t, url: null, at: t.at, offline: !t.online || t.offline, error: null };
    this.link.set(LINK_CAM, t.cam.id);
    this.syncLoops();   // grid pauses while the large view refreshes (hq=1)
  }

  /** Every close path (✕, backdrop, Escape, back / forward). deactivate() leaves the link alone: a lost session
   *  keeps the camera for the next login; a login without TOTP drops it (fetchStatus). */
  closeLarge(): void {
    if (!this.large) return;
    this.retire(this.large.url);
    this.large = null;
    this.link.set(LINK_CAM, null);
    this.syncLoops();
    this.cdr.markForCheck();
  }

  // ── Deep link ───────────────────────────────────────────────────────────────

  private camFromLink(): string | null {
    const id = this.link.get(LINK_CAM);
    return id && CAM_ID.test(id) ? id : null;
  }

  /** Back / forward or an edited link: open, switch or close the large view. Read-only (snapshots only). */
  private applyLink(): void {
    if (this.link.get('admin') !== 'cams') return;   // this section is closing: nothing to apply
    const id = this.camFromLink();
    if (id === (this.large?.tile.cam.id ?? null)) { this.linkedCam = null; return; }
    this.linkedCam = id;
    if (!id) this.closeLarge();
    else if (this.active && this.camsLoaded) this.openLinked();
    this.cdr.markForCheck();
  }

  /** Opens the linked camera's large view (true), or drops a camera that is not in the list. */
  private openLinked(): boolean {
    const id = this.linkedCam;
    this.linkedCam = null;
    const t = id ? this.tiles.find(x => x.cam.id === id) : undefined;
    if (t) { this.openLarge(t); this.cdr.markForCheck(); return true; }
    if (id) this.link.set(LINK_CAM, null);
    return false;
  }

  // ── All cameras (choose CAMS_ALLOWLIST) ─────────────────────────────────────

  toggleAll(): void {
    this.allOpen = !this.allOpen;
    if (this.allOpen && !this.allRows.length) this.fetchAll();
  }

  async fetchAll(): Promise<void> {
    const epoch = this.epoch;
    // Deactivated meanwhile: drop the answer (deactivate() cleared the list and the loading state)
    const stale = () => this.destroyed || epoch !== this.epoch || !this.active;
    this.allLoading = true;
    this.allError = null;
    this.cdr.markForCheck();
    let r: Response | null = null;
    try { r = await this.camFetch('/api/admin/cams/all'); }
    catch (e: any) { if (!stale()) this.allError = e?.message || String(e); }
    if (stale()) return;
    this.allLoading = false;
    if (!r) { this.cdr.markForCheck(); return; }
    if (!r.ok) {
      const err = await this.errorText(r);
      if (!stale()) { this.allError = err; this.cdr.markForCheck(); }
      return;
    }
    try {
      const data: any = await r.json();
      if (stale()) return;
      const list: CamAllInfo[] = Array.isArray(data) ? data : Array.isArray(data?.cameras) ? data.cameras : [];
      this.allRows = list
        .filter(c => c && typeof c.id === 'string' && CAM_ID.test(c.id))
        .map(c => ({ c, name: c.name || c.id, selected: !!c.allowlisted }))
        .sort((a, b) => a.name.localeCompare(b.name));
      this.updateAllowlistLine();
    } catch { this.allError = 'JSON'; }
    this.cdr.markForCheck();
  }

  toggleSelect(row: AllRow): void {
    row.selected = !row.selected;
    this.updateAllowlistLine();
  }

  private updateAllowlistLine(): void {
    this.allowlistLine = 'CAMS_ALLOWLIST=' + this.allRows.filter(r => r.selected).map(r => r.c.id).join(',');
  }

  get allowlistEmpty(): boolean { return !this.allRows.some(r => r.selected); }

  // ── Audit ───────────────────────────────────────────────────────────────────

  toggleAudit(): void {
    this.auditOpen = !this.auditOpen;
    if (this.auditOpen) this.fetchAudit();
  }

  async fetchAudit(): Promise<void> {
    const epoch = this.epoch;
    const stale = () => this.destroyed || epoch !== this.epoch || !this.active;
    this.auditLoading = true;
    this.auditError = null;
    this.cdr.markForCheck();
    let r: Response | null = null;
    try { r = await this.camFetch('/api/admin/cams/audit'); }
    catch (e: any) { if (!stale()) this.auditError = e?.message || String(e); }
    if (stale()) return;
    this.auditLoading = false;
    if (!r) { this.cdr.markForCheck(); return; }
    if (!r.ok) {
      const err = await this.errorText(r);
      if (!stale()) { this.auditError = err; this.cdr.markForCheck(); }
      return;
    }
    try {
      const data: any = await r.json();
      if (stale()) return;
      const list: CamAuditEntry[] = Array.isArray(data) ? data : Array.isArray(data?.entries) ? data.entries : [];
      const locale = this.locale;
      this.auditRows = list
        .map((e, i) => {
          const ms = this.toMs(e.ts) ?? 0;
          const d = new Date(ms);
          const action = String(e.action ?? '');
          const sess = String(e.session ?? e['sessionHash'] ?? e['sessionPrefix'] ?? '');
          return {
            key: ms * 1000 + i,
            time: d.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            full: d.toLocaleString(locale),
            action,
            label: this.auditLabel(action),
            cls: action === 'login-failed' ? 'text-bg-danger' : action === 'login' ? 'text-bg-success'
               : action === 'logout' ? 'text-bg-secondary' : 'text-bg-info',
            session: sess === '-' ? '' : sess,   // '-' = no session (failed login)
            ip: String(e.ip ?? e['clientIp'] ?? ''),
          };
        })
        .sort((a, b) => b.key - a.key);
    } catch { this.auditError = 'JSON'; }
    this.cdr.markForCheck();
  }

  private auditLabel(action: string): string {
    const key = 'ADMIN_CAMS_AUDIT_' + action.toUpperCase().replace(/[^A-Z]/g, '_');
    const s = this.translate.instant(key) as string;
    return s === key ? action : s;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  copy(text: string, what: string): void {
    const done = () => {
      this.copied = what;
      this.cdr.markForCheck();
      setTimeout(() => { if (this.copied === what) { this.copied = null; this.cdr.markForCheck(); } }, 1500);
    };
    const fallback = () => {
      // Plain http (no clipboard API): copy through a temporary textarea
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { if (document.execCommand('copy')) done(); } catch { /* nothing to do */ }
      ta.remove();
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, fallback);
    else fallback();
  }

  stateLabel(state?: string): string {
    if (!state) return '?';
    const key = 'ADMIN_CAMS_STATE_' + state.toUpperCase();
    const s = this.translate.instant(key) as string;
    return s === key ? state : s;
  }

  /** Protect says CONNECTED but the snapshot answered 503: show it as offline like the placeholder does. */
  tileState(t: CamTile, offline = t.offline): string | undefined {
    return offline && (t.cam.state === 'CONNECTED' || !t.cam.state) ? 'DISCONNECTED' : t.cam.state;
  }

  stateCls(state?: string): string {
    return state === 'CONNECTED' ? 'text-bg-success' : state === 'CONNECTING' ? 'text-bg-warning' : state ? 'text-bg-danger' : 'text-bg-secondary';
  }

  /**
   * fetch with the session header and no HTTP cache. 401 ends the admin session (→ null).
   * `abortable`: the loops' requests (list, snapshots) are cancelled when the loops restart or stop.
   */
  private async camFetch(url: string, init: RequestInit = {}, abortable = false): Promise<Response | null> {
    let r: Response;
    try {
      r = await fetch(url, {
        cache: 'no-store', signal: abortable ? this.abort?.signal : undefined, ...init,
        headers: { ...this.authHeaders(), ...((init.headers as Record<string, string> | undefined) ?? {}) },
      });
    } catch (e: any) {
      if (e?.name === 'AbortError') return null;
      throw e;
    }
    if (this.destroyed) return null;
    if (r.status === 401) { this.deactivate(); this.unauthorized.emit(); return null; }
    return r;
  }

  private async readError(r: Response): Promise<{ text: string; code: string }> {
    let text = `HTTP ${r.status}`;
    let code = '';
    try {
      const body = await r.text();
      try {
        const j = JSON.parse(body);
        if (j?.error) { code = String(j.error); text += ` – ${j.error}`; }
      } catch { if (body && body.length < 200) text += ` – ${body}`; }
    } catch { /* no body */ }
    return { text, code };
  }

  private async errorText(r: Response): Promise<string> {
    return (await this.readError(r)).text;
  }

  /** Object URLs are revoked shortly after being replaced (the <img> has switched by then). */
  private retire(url: string | null): void {
    if (!url) return;
    this.retiring.add(url);
    setTimeout(() => { if (this.retiring.delete(url)) URL.revokeObjectURL(url); }, 1000);
  }

  private revokeAll(): void {
    for (const t of this.tiles) { if (t.url) URL.revokeObjectURL(t.url); t.url = null; }
    if (this.large?.url) { URL.revokeObjectURL(this.large.url); this.large.url = null; }
    this.retiring.forEach(u => URL.revokeObjectURL(u));
    this.retiring.clear();
  }

  /** Epoch ms from ms or seconds; null when absent. */
  private toMs(v: unknown): number | null {
    const n = Number(v);
    if (v === null || v === undefined || !Number.isFinite(n) || n <= 0) return null;
    return n < 1e12 ? n * 1000 : n;
  }

  private clock(): string {
    return new Date().toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  private get locale(): string {
    return uiLocale();
  }
}
